import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BEHAVIOR_LEVELS_DEFAULT } from '@/data/mockCatalog';
import type { NotifLang } from '@/types/admin.types';
import { LangBar, LANG_OPTIONS } from '../tabs/NotificationsTab/LangBar';

const LS_KEY = 'pmp_behavior_levels';
const LANG_KEYS = LANG_OPTIONS.map(l => l.id) as NotifLang[];
const emptyLabels = (): Record<NotifLang, string> =>
  Object.fromEntries(LANG_KEYS.map(k => [k, ''])) as Record<NotifLang, string>;

function withLabels<T extends { label: string; labels?: Record<string, string> }>(items: T[]) {
  return items.map(l => ({
    ...l,
    labels: l.labels ?? { ...emptyLabels(), es: l.label },
  }));
}

function loadLevels() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return withLabels(JSON.parse(raw) as typeof BEHAVIOR_LEVELS_DEFAULT);
  } catch { /* ignore */ }
  return withLabels(BEHAVIOR_LEVELS_DEFAULT.map(l => ({ ...l })));
}

export function NivelesAdopcionEditor() {
  const { t, i18n } = useTranslation();
  const [levels, setLevels] = useState(loadLevels);
  const [editing, setEditing] = useState(false);
  const [activeLang, setActiveLang] = useState<NotifLang>('es');

  function saveLevels() {
    const toSave = levels.map(l => ({ ...l, label: l.labels?.es ?? l.label }));
    try { localStorage.setItem(LS_KEY, JSON.stringify(toSave)); } catch { /* ignore */ }
    setLevels(toSave);
    setEditing(false);
  }

  function getRange(i: number) {
    const next = levels[i + 1];
    if (!next) return `${levels[i].minScore.toFixed(2)} – 4.00`;
    return `${levels[i].minScore.toFixed(2)} – ${(next.minScore - 0.01).toFixed(2)}`;
  }

  const uiLang = (i18n.language?.slice(0, 2) ?? 'es') as NotifLang;

  const filledCount = (labels: Record<string, string> | undefined) =>
    LANG_KEYS.filter(k => (labels?.[k] ?? '').trim() !== '').length;

  return (
    <div className="sc-editor-wrap">
      {editing ? (
        <>
          <div className="sc-editor-header">
            <span>
              <i className="fas fa-layer-group" style={{ color: '#F08521' }} />{' '}
              {t('admin.nivelesAdopcionEditTitle')}
            </span>
            <button
              className="sc-edit-btn sc-edit-btn--cancel"
              onClick={() => { setLevels(withLabels(BEHAVIOR_LEVELS_DEFAULT.map(l => ({ ...l })))); setEditing(false); }}
            >
              <i className="fas fa-times" />
            </button>
          </div>
          <LangBar active={activeLang} onSwitch={setActiveLang} group="niveles-label" />
          <div className="sc-edit-form">
            {levels.map((lv, i) => (
              <div key={`level-${i}`} className="sc-edit-row">
                <div
                  style={{
                    width: 12, height: 12, borderRadius: '50%',
                    background: lv.color, flexShrink: 0,
                  }}
                />
                <input
                  className="sc-edit-label"
                  value={lv.labels?.[activeLang] ?? ''}
                  placeholder={t('admin.madurezLabelPlaceholder')}
                  onChange={e =>
                    setLevels(prev => prev.map((x, j) =>
                      j === i ? { ...x, labels: { ...emptyLabels(), ...x.labels, [activeLang]: e.target.value } } : x
                    ))
                  }
                />
                {i > 0
                  ? (
                    <input
                      className="sc-edit-thresh"
                      type="number"
                      min={1}
                      max={3.9}
                      step={0.05}
                      value={lv.minScore}
                      onChange={e =>
                        setLevels(prev => prev.map((x, j) =>
                          j === i ? { ...x, minScore: parseFloat(e.target.value) || x.minScore } : x
                        ))
                      }
                    />
                  )
                  : <span className="sc-edit-fixed">≥ 1.0</span>
                }
                <span className="sc-lang-count">{filledCount(lv.labels)} / 7</span>
                <input
                  type="color"
                  className="sc-edit-color"
                  value={lv.color}
                  onChange={e =>
                    setLevels(prev => prev.map((x, j) => j === i ? { ...x, color: e.target.value } : x))
                  }
                />
              </div>
            ))}
          </div>
          <div className="sc-edit-actions">
            <button className="sc-save-btn" onClick={saveLevels}>
              <i className="fas fa-check" /> {t('common.save')}
            </button>
            <button
              className="sc-reset-btn"
              onClick={() => {
                setLevels(withLabels(BEHAVIOR_LEVELS_DEFAULT.map(l => ({ ...l }))));
                try { localStorage.removeItem(LS_KEY); } catch { /* ignore */ }
                setEditing(false);
              }}
            >
              <i className="fas fa-undo" /> {t('admin.restoreDefaults')}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="sc-editor-header">
            <span>
              <i className="fas fa-layer-group" style={{ color: '#F08521' }} />{' '}
              {t('admin.nivelesAdopcionTitle')}
            </span>
            <button className="sc-edit-btn" onClick={() => setEditing(true)}>
              <i className="fas fa-pen" /> {t('common.edit')}
            </button>
          </div>
          <div className="sc-readonly-list">
            {levels.map((lv, i) => (
              <div
                key={`level-ro-${i}`}
                className="sc-readonly-item"
                style={{ borderLeft: `3px solid ${lv.color}` }}
              >
                <div
                  className="sc-readonly-label"
                  style={{ color: lv.color, background: lv.color + '18' }}
                >
                  {lv.labels?.[uiLang] || lv.labels?.es || lv.label}
                </div>
                <div className="sc-readonly-range">{getRange(i)}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
